/**
 * ProfileSetup page - Second step of onboarding
 */

import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button} from '../../components/ui/Button';
import {Input} from '../../components/ui/Input';
import {Container} from '../../components/ui/Container';
import {ProgressBar} from '../../components/onboarding/ProgressBar';
import {useOnboardingStore, useAuthStore} from '@voxapilot/shared/stores';
import {ONBOARDING_STEPS} from '@voxapilot/shared/types';
import {ROUTES} from '../../config/constants';
import {api} from '../../config/firebase';

export function ProfileSetupPage() {
  const navigate = useNavigate();
  const {completeStep, updateOnboardingData} = useOnboardingStore();
  const {profile, setProfile} = useAuthStore();
  
  const [formData, setFormData] = useState({
    displayName: profile?.displayName || '',
    bio: profile?.bio || '',
    company: profile?.company || '',
    jobTitle: profile?.jobTitle || '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Update profile via API
      const response = await api.auth.updateProfile({
        displayName: formData.displayName,
        bio: formData.bio,
        company: formData.company,
        jobTitle: formData.jobTitle,
        onboardingData: {
          profileSetup: true,
        },
      });

      if (response.success && response.data) {
        setProfile(response.data);
      }
      updateOnboardingData({profileSetup: true});
      completeStep(ONBOARDING_STEPS.PROFILE_SETUP);
      navigate(ROUTES.ONBOARDING_FIRST_MEETING);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    completeStep(ONBOARDING_STEPS.PROFILE_SETUP);
    navigate(ROUTES.ONBOARDING_FIRST_MEETING);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      <Container className="flex-1 flex flex-col items-center justify-center py-12">
        <div className="max-w-2xl w-full space-y-8">
          {/* Progress */}
          <div className="mb-8">
            <ProgressBar />
          </div>

          {/* Content */}
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-3xl sm:text-4xl font-heading font-bold text-textPrimary dark:text-gray-100">
                Complete Your Profile
              </h1>
              <p className="text-textSecondary dark:text-gray-400">
                Help us personalize your experience
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Display Name *
                </label>
                <Input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                  placeholder="Your name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Company
                </label>
                <Input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Your company"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Job Title
                </label>
                <Input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                  placeholder="Your role"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Bio
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  placeholder="Tell us about yourself"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-textPrimary focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-0"
                  rows={4}
                />
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isLoading || !formData.displayName}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? 'Saving...' : 'Continue'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="lg"
                  onClick={handleSkip}
                  className="w-full sm:w-auto"
                >
                  Skip for now
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Container>
    </div>
  );
}

